import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  projectProposalOperations,
  readProposalOperationValue,
} from "work/proposals/negotiation/proposal-operation-projector";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { proposalSemanticJsonValuesEqual } from "work/proposals/negotiation/proposal-collection-codec";

export type ProposalOperationConflict = {
  operation: ProposalContentOperation;
  currentValue: unknown;
};

export type ProposalOperationMergeResult = {
  projection: ProposalJSON;
  appliedOperationIds: string[];
  conflicts: ProposalOperationConflict[];
};

/**
 * Three-way semantic merge used by the contract tests and mirrored by the API
 * command handler. A stale command is valid whenever the value it actually
 * edits still matches its expectation; unrelated stream activity is irrelevant.
 */
export function mergeProposalOperations(
  projection: ProposalJSON,
  operations: ProposalContentOperation[],
): ProposalOperationMergeResult {
  let mergedProjection = structuredClone(projection);
  const appliedOperationIds: string[] = [];
  const conflicts: ProposalOperationConflict[] = [];

  for (const operation of operations) {
    // Resolution decisions are merged against envelope resolution state by the
    // command service. This helper models proposal JSON only.
    if (operation.kind === "ResolutionDecision") {
      appliedOperationIds.push(operation.operationId);
      continue;
    }
    const currentValue = readSemanticValue(mergedProjection, operation);
    if (operationAlreadyApplied(currentValue, operation)) {
      appliedOperationIds.push(operation.operationId);
      continue;
    }
    if (!expectedValueMatches(currentValue, operation)) {
      conflicts.push({ operation, currentValue });
      continue;
    }

    mergedProjection = projectProposalOperations(mergedProjection, [operation]);
    appliedOperationIds.push(operation.operationId);
  }

  return { projection: mergedProjection, appliedOperationIds, conflicts };
}

function readSemanticValue(
  projection: ProposalJSON,
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): unknown {
  if (operation.kind === "FieldSet") return readProposalOperationValue(projection, operation.path);
  const collection = readProposalOperationValue(projection, operation.path);
  if (!Array.isArray(collection)) return undefined;
  return collection.find((item) => proposalCollectionItemHasIdentity(item, operation.itemId));
}

/**
 * Whether an operation's stale expectation is already reflected in the current merged value.
 *
 * Uses the semantic (`null`/`undefined`-equivalent) comparison deliberately: this three-way merge
 * compares a queued value against live server-projected state, exactly the same shape of comparison
 * `proposalSemanticJsonValuesEqual`'s other call sites make against resolution/baseline state — not a
 * diff of one value against its own prior self, where the stricter distinction matters (see
 * `proposalStrictJsonValuesEqual`'s doc comment).
 */
function operationAlreadyApplied(
  currentValue: unknown,
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): boolean {
  switch (operation.kind) {
    case "FieldSet":
      return proposalSemanticJsonValuesEqual(currentValue, operation.value);
    case "CollectionItemAdded":
      return proposalSemanticJsonValuesEqual(currentValue, operation.value);
    case "CollectionItemUpdated":
      return valueContains(currentValue, operation.value);
    case "CollectionItemRemoved":
      return currentValue === undefined;
  }
}

function expectedValueMatches(
  currentValue: unknown,
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): boolean {
  if (operation.kind === "CollectionItemAdded") return currentValue === undefined;
  return valueContains(currentValue, operation.expectedValue);
}

function valueContains(currentValue: unknown, expectedValue: unknown): boolean {
  if (!isRecord(expectedValue)) return proposalSemanticJsonValuesEqual(currentValue, expectedValue);
  if (!isRecord(currentValue)) return false;
  return Object.entries(expectedValue).every(([key, value]) => valueContains(currentValue[key], value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
